/** @jsx jsx */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { EnterpriseLearnerInsights_MetricTimeFrame as MetricTimeframe } from '__generated__/graphql-types';

import { Grid, Typography2, breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import _t from 'i18n!nls/enterprise-admin-home';

const styles = {
  wrapper: css`
    background-color: var(--cds-color-white-0);
    border-radius: 3px;
    border: 1px solid var(--cds-color-grey-100);
    width: 100%;
  `,
  header: css`
    padding: var(--cds-spacing-200) var(--cds-spacing-300);
    border-bottom: 1px solid var(--cds-color-grey-100);
  `,
  body: css`
    padding: 0 var(--cds-spacing-300);

    ${breakpoints.only('xs')} {
      padding: 0 var(--cds-spacing-150);
    }
  `,
  tag: css`
    margin-left: var(--cds-spacing-100);
    text-transform: uppercase;
  `,
  timeframe: css`
    margin-left: auto;
  `,
};

type Props = {
  header: string;
  headerTag?: React.ReactNode;
  timeframe?: MetricTimeframe;
  footer?: React.ReactNode;
  customCss?: Interpolation<Theme>;
};

const LearningInsightsCard: React.LegacyFunctionComponentWithChildren<Props> = ({
  children,
  footer,
  header,
  headerTag,
  timeframe,
  customCss,
}) => {
  return (
    <div css={[styles.wrapper, customCss ?? {}]}>
      <Grid container direction="row" alignItems="center" css={styles.header}>
        <Typography2 variant="subtitleLarge" component="h3">
          {header}
        </Typography2>
        {!!headerTag && <span css={styles.tag}>{headerTag}</span>}
        {!!timeframe && (
          <Typography2 color="supportText" component="span" css={styles.timeframe}>
            {
              {
                [MetricTimeframe.ThirtyDays]: _t('Last 30 days'),
                [MetricTimeframe.SixtyDays]: _t('Last 60 days'),
                [MetricTimeframe.NinetyDays]: _t('Last 90 days'),
              }[timeframe]
            }
          </Typography2>
        )}
      </Grid>
      <div css={styles.body}>
        {children}
        {footer}
      </div>
    </div>
  );
};

export default LearningInsightsCard;
